import React from 'react';
import { SearchIcon, BellIcon, ChevronDownIcon, HomeIcon, GaugeIcon, BarChart3Icon, WrenchIcon, FileTextIcon, LayoutGridIcon, DropletIcon, AlertTriangleIcon, UsersIcon, LeafIcon, CreditCardIcon, CheckCircle2Icon, LoaderCircleIcon } from 'lucide-react';
import { solutionDashboard as d, type Bi } from '../../../data/waterSolution';
type P = {
  isFR: boolean;
};
// Exact logo files used in the site header (components/Header.tsx)
const BCITI_LOGO = 'https://cdn.magicpatterns.com/uploads/kFQruRR3oauw7NNUdgFajn/Logo_BCITI_(5).png';
const ATNA_LOGO = 'https://cdn.magicpatterns.com/uploads/d6cqpG2SGUofNqC1hEedXv/Logo_ATNA_(5).png';
const NAV_ICONS = {
  overview: LayoutGridIcon,
  properties: HomeIcon,
  meters: GaugeIcon,
  consumption: BarChart3Icon,
  alerts: BellIcon,
  users: UsersIcon,
  payments: CreditCardIcon,
  interventions: WrenchIcon,
  reports: FileTextIcon
} as const;
const KPI_ICON = {
  meters: {
    Icon: DropletIcon,
    bg: '#EEF0FE',
    fg: '#4F46E5'
  },
  high: {
    Icon: AlertTriangleIcon,
    bg: '#FDECEC',
    fg: '#E5484D'
  },
  leak: {
    Icon: UsersIcon,
    bg: '#EEF0FE',
    fg: '#4F46E5'
  },
  irrigation: {
    Icon: LeafIcon,
    bg: '#E8F6EF',
    fg: '#1F9D7A'
  }
} as const;
const MARKER = {
  red: '#E5484D',
  orange: '#F28C38',
  indigo: '#4F46E5'
} as const;
const PANEL = 'rounded-xl border border-[#EEEEF4] bg-white';
function Bars({
  isFR
}: P) {
  const days = isFR ? d.days.fr : d.days.en;
  return <div className="flex gap-2" data-id="element-7371" data-mp="2lejemolhxgdq">
      <div className="flex flex-col justify-between h-[92px] font-sans text-[8px] text-[#9AA0B2] pb-4" aria-hidden="true" data-id="element-7372" data-mp="2ldrm5mn73yro">
        {[400, 300, 200, 100, 0].map(v => <span key={v} data-id="element-7373" data-mp="2v54cg9tcigvl">{v}</span>)}
      </div>
      <ul className="flex-1 flex items-end justify-between gap-1.5 h-[92px]" data-id="element-7374" data-mp="2q6ucllwoes3n">
        {d.values.map((v, i) => <li key={days[i]} className="flex-1 flex flex-col items-center gap-1" data-id="element-7375" data-mp="345kedeufxfyc" data-mpi={i}>
            <span className="w-full max-w-[18px] rounded-t-[3px]" style={{
          height: `${v / 400 * 76}px`,
          background: i === d.values.length - 1 ? '#4F46E5' : '#DCDDFB'
        }} aria-hidden="true" data-id="element-7376" data-mp="3050gua5ygabo" />
            <span className="font-sans text-[8px] text-[#8A90A2]" data-id="element-7377" data-mp="3c7jjdbwlpjix">{days[i]}</span>
          </li>)}
      </ul>
    </div>;
}
function MapPanel() {
  return <div className="relative h-[104px] rounded-lg overflow-hidden bg-[#F1F2F6]" aria-hidden="true" data-id="element-7378" data-mp="3lx34ko7sdpgy">
      <svg viewBox="0 0 200 104" preserveAspectRatio="none" className="absolute inset-0 w-full h-full" data-id="element-7379" data-mp="3pz7wniil6e1h">
        <path d="M140 0 C 150 26, 180 34, 170 60 C 164 82, 186 94, 200 104 L200 0 Z" fill="#CFE0F8" data-id="element-7380" data-mp="3uwq4f5kvan7w" />
        <path d="M0 34 L200 26 M18 0 L56 104 M0 78 L200 70 M100 0 L118 104" stroke="#FFFFFF" strokeWidth="3" fill="none" data-id="element-7381" data-mp="3s9niljb1ajl" />
        <path d="M0 16 L200 52 M36 104 L84 0" stroke="#E2E4EC" strokeWidth="1.5" fill="none" data-id="element-7382" data-mp="8pt1dkc3blb0" />
      </svg>
      {d.markers.map((m, i) => <span key={i} className="absolute -translate-x-1/2 -translate-y-1/2 w-4 h-4 rounded-full border-2 border-white shadow-sm" style={{
      left: `${m.x}%`,
      top: `${m.y}%`,
      background: MARKER[m.tone]
    }} data-id="element-7383" data-mp="2ahoaptavjdq2" data-mpi={i} />)}
    </div>;
}
export function SolutionLaptop({
  isFR,
  showActions = true
}: P & {
  showActions?: boolean;
}) {
  const pick = (v: Bi) => isFR ? v.fr : v.en;
  return <div data-id="element-7384" data-mp="dvonmx4tpgzc">
      <div className="rounded-t-[22px] bg-[#1B1D26] p-[9px] pb-[12px] shadow-[0_40px_70px_-40px_rgba(20,20,60,0.55)]" data-id="element-7385" data-mp="dvy86573mvxn">
        <div className="rounded-[14px] bg-[#FCFCFE] overflow-hidden" data-id="element-7386" data-mp="1ak4dkubuhscz">
          <div className="flex items-center gap-3 px-4 h-[50px] bg-white border-b border-[#F2F2F7]" data-id="element-7387" data-mp="16jkjjzmkj4v2">
            <img src={BCITI_LOGO} alt="BCITI" className="h-[19px] w-auto object-contain" data-id="element-7388" data-mp="11jy55mv07iko" />
            <span className="text-[#121212]/25 font-sans text-sm font-light" aria-hidden="true" data-id="element-7389" data-mp="wm64a3cdsm4c">|</span>
            <img src={ATNA_LOGO} alt="ATNA" className="h-[15px] w-auto object-contain" data-id="element-7390" data-mp="3mb8fpstrctct" />
            <div className="hidden sm:flex flex-1 min-w-0 items-center gap-2 rounded-lg bg-[#F5F6FA] px-3 py-1.5 ml-2" data-id="element-7391" data-mp="3hbcaxrd8kxw7">
              <SearchIcon className="w-3 h-3 text-[#9AA0B2] shrink-0" aria-hidden="true" data-id="element-7392" data-mp="1924gpgb22ydz" />
              <span className="font-sans text-[9.5px] text-[#9AA0B2] truncate" data-id="element-7393" data-mp="1du5nwntd9yx5">{pick(d.search)}</span>
            </div>
            <span className="ml-auto sm:ml-0 flex items-center gap-1.5 rounded-lg border border-[#EEEEF4] px-2.5 py-1.5 font-sans text-[9.5px] font-medium text-[#121A3A] whitespace-nowrap" data-id="element-7394" data-mp="103sfkkckci7a">
              {pick(d.org)}
              <ChevronDownIcon className="w-3 h-3" aria-hidden="true" data-id="element-7395" data-mp="1sry139bfyb86" />
            </span>
            <BellIcon className="w-3.5 h-3.5 text-[#5B6478] shrink-0" aria-hidden="true" data-id="element-7396" data-mp="1ws8a6uuj5hr9" />
            <span className="w-7 h-7 rounded-full bg-[#4F46E5] flex items-center justify-center font-sans text-[9px] font-semibold text-white shrink-0" aria-hidden="true" data-id="element-7397" data-mp="1is7ahiq04gdd">
              LG
            </span>
          </div>
          <div className="flex" data-id="element-7398" data-mp="7fwmrqq47afs">
            <ul className="hidden sm:block w-[118px] shrink-0 bg-white border-r border-[#F2F2F7] p-2 space-y-0.5" data-id="element-7399" data-mp="ccn56kpe6i4d">
              {d.nav.map(n => {
              const Icon = NAV_ICONS[n.key];
              const active = n.key === 'overview';
              return <li key={n.key} className={`flex items-center gap-2 rounded-lg px-2 py-[7px] font-sans text-[10.5px] whitespace-nowrap ${active ? 'bg-[#EEEDFD] text-[#4F46E5] font-semibold' : 'text-[#3A4258]'}`} data-id="element-7400" data-mp="3w21cuv83fkh2">
                    <Icon className={`w-3.5 h-3.5 shrink-0 ${active ? 'text-[#4F46E5]' : 'text-[#8A90A2]'}`} strokeWidth={1.8} aria-hidden="true" data-id="element-7401" data-mp="3d9h2atw9i222" />
                    {pick(n.label)}
                  </li>;
            })}
            </ul>
            <div className="flex-1 min-w-0 p-3" data-id="element-7402" data-mp="3m8kkzwriofg1">
              <ul className="grid grid-cols-2 md:grid-cols-4 gap-2" data-id="element-7403" data-mp="2yjsynm0lyrb6">
                {d.kpis.map(k => {
                const {
                  Icon,
                  bg,
                  fg
                } = KPI_ICON[k.key];
                return <li key={k.key} className={`${PANEL} p-2.5 flex items-start gap-2`} data-id="element-7404" data-mp="xid1o7u8kmoq">
                      <span className="w-8 h-8 rounded-full flex items-center justify-center shrink-0" style={{
                    background: bg
                  }} aria-hidden="true" data-id="element-7405" data-mp="12gwmg4x3fsfi">
                        <Icon className="w-4 h-4" style={{
                      color: fg
                    }} strokeWidth={2} data-id="element-7406" data-mp="178eokwaurz22" />
                      </span>
                      <span className="min-w-0" data-id="element-7407" data-mp="1c92ch93sbzs2">
                        <span className="block font-sans text-[16px] font-semibold text-[#121A3A] leading-none" data-id="element-7408" data-mp="ek0ped812wox">{pick(k.value)}</span>
                        <span className="block font-sans text-[9px] text-[#5B6478] mt-1 leading-tight" data-id="element-7409" data-mp="jiu0y03r8dmg">{pick(k.label)}</span>
                        {k.trend && <span className={`block font-sans text-[8.5px] font-semibold mt-0.5 ${k.tone === 'bad' ? 'text-[#E5484D]' : 'text-[#1F9D7A]'}`} data-id="element-7410" data-mp="sj8qjbnr4evr">{pick(k.trend)}</span>}
                      </span>
                    </li>;
              })}
              </ul>
              <div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-2" data-id="element-7411" data-mp="3of4w4hnq1sfa">
                <div className={`${PANEL} p-3`} data-id="element-7412" data-mp="3keuol0ka6tyb">
                  <p className="font-sans text-[10.5px] font-semibold text-[#121A3A] mb-2" data-id="element-7413" data-mp="3kf4gitovzb6n">{pick(d.chartTitle)}</p>
                  <Bars isFR={isFR} data-id="element-7414" data-mp="3flrtgnzuvcg0" />
                </div>
                <div className={`${PANEL} p-3`} data-id="element-7415" data-mp="3anqf2gddk1av">
                  <div className="flex items-center justify-between mb-2" data-id="element-7416" data-mp="3amyqhpi733u3">
                    <p className="font-sans text-[10.5px] font-semibold text-[#121A3A]" data-id="element-7417" data-mp="35qzxtppu2znv">{pick(d.mapTitle)}</p>
                    <span className="flex items-center gap-1 font-sans text-[8.5px] text-[#5B6478]" data-id="element-7418" data-mp="30sq3tifui53u">
                      {pick(d.mapFilter)}
                      <ChevronDownIcon className="w-2.5 h-2.5" aria-hidden="true" data-id="element-7419" data-mp="2rqgw10byopu5" />
                    </span>
                  </div>
                  <MapPanel data-id="element-7420" data-mp="ux57upjy656e" />
                </div>
              </div>
              {showActions && <div className={`${PANEL} p-3 mt-2`} data-id="element-7421" data-mp="pz3oe3im03ue">
                <p className="font-sans text-[10.5px] font-semibold text-[#121A3A] mb-1.5" data-id="element-7422" data-mp="3uy2m0b7rwh0w">{pick(d.actionsTitle)}</p>
                <table className="w-full font-sans text-[9px] text-[#3A4258]" data-id="element-7423" data-mp="3sksvjdgkbta">
                  <thead data-id="element-7424" data-mp="3l92o6v50broa">
                    <tr className="text-[#9AA0B2] text-left" data-id="element-7425" data-mp="3l7rkswfh4lxp">
                      <th className="font-normal py-1" data-id="element-7426" data-mp="3q7m9ctbs0zfl">{pick(d.cols.date)}</th>
                      <th className="font-normal py-1" data-id="element-7427" data-mp="3q5j97ggwtpx5">{pick(d.cols.property)}</th>
                      <th className="font-normal py-1 hidden md:table-cell" data-id="element-7428" data-mp="3c6tcw31wzu9u">{pick(d.cols.type)}</th>
                      <th className="font-normal py-1" data-id="element-7429" data-mp="3gb13y6vuhmb8">{pick(d.cols.status)}</th>
                    </tr>
                  </thead>
                  <tbody data-id="element-7430" data-mp="379091go33ccq">
                    {d.actions.map(a => <tr key={a.property.en} className="border-t border-[#F2F2F7]" data-id="element-7431" data-mp="37b489lb1fn6o">
                        <td className="py-1 text-[#8A90A2] whitespace-nowrap pr-2" data-id="element-7432" data-mp="10j3jcuj9dypw">{pick(a.date)}</td>
                        <td className="py-1 whitespace-nowrap pr-2" data-id="element-7433" data-mp="15gvjucxyyt5p">{pick(a.property)}</td>
                        <td className="py-1 hidden md:table-cell whitespace-nowrap pr-2" data-id="element-7434" data-mp="2q07ci5ku0xn0">{pick(a.type)}</td>
                        <td className="py-1" data-id="element-7435" data-mp="2l2fnpkfhhrn1">
                          <span className="flex items-center gap-1 whitespace-nowrap" data-id="element-7436" data-mp="2zq0ov8sstgsb">
                            {a.tone === 'done' ? <CheckCircle2Icon className="w-3 h-3 text-[#1F9D7A]" aria-hidden="true" data-id="element-7437" data-mp="2ux7kwn55mcha" /> : <LoaderCircleIcon className="w-3 h-3 text-[#4F46E5]" aria-hidden="true" data-id="element-7438" data-mp="33r44pvaujctj" />}
                            {pick(a.status)}
                          </span>
                        </td>
                      </tr>)}
                  </tbody>
                </table>
              </div>}
            </div>
          </div>
        </div>
      </div>
      <div className="h-[14px] mx-[-3%] rounded-b-[14px] bg-[#C9CBD3] shadow-[0_24px_40px_-24px_rgba(20,20,60,0.6)]" aria-hidden="true" data-id="element-7439" data-mp="1jbsh4kcotc0q" />
    </div>;
}